import React from 'react'

import { capitalize } from 'es-toolkit'
import { Form, FormikProvider, useFormik } from 'formik'
import type { FC } from 'react'
import * as Yup from 'yup'

import { Button } from '@/components/ui/button'
import { DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { canCreate } from '@/pages/Admin/helpers'
import { SYSTEM_ID } from '@/pages/Admin/system-settings/constants'
import type { AssetWithParent, TagWithParent, Variant } from '@/shared/types'

import { useCopyAndMove } from './hooks/useCopyAndMove'

interface Props {
	onSave: (name: string, newParentId: string) => void
	onClose: () => void
	type: 'copy' | 'move'
	name: string
	isAdmin: boolean
	selectedApp: Variant
	resource: AssetWithParent | TagWithParent
}

const CopyAndMoveForm: FC<Props> = ({
	onSave,
	onClose,
	name = '',
	type,
	isAdmin,
	selectedApp,
	resource,
}) => {
	const {
		selectedWorkspace,
		selectedApplication,
		selectedVar,
		onResourceClick,
		onBreadcrumbClick,
		selectableResources,
		breadcrumbResources,
	} = useCopyAndMove({ resource, selectedVariant: selectedApp, isAdmin })

	const formik = useFormik({
		initialValues: { name },
		enableReinitialize: true,
		validationSchema: Yup.object().shape({
			name: Yup.string().max(255).required('Name is required'),
		}),
		onSubmit: (values, { setSubmitting }) => {
			const newParentId =
				selectedVar?._id ??
				selectedApplication?._id ??
				selectedWorkspace?._id ??
				(isAdmin && SYSTEM_ID)

			if (!newParentId) return

			onSave(values.name, newParentId)
			setSubmitting(false)
		},
	})

	const {
		errors,
		touched,
		handleSubmit,
		isSubmitting,
		getFieldProps,
		setFieldValue,
		values,
	} = formik

	return (
		<FormikProvider value={formik}>
			<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
				<DialogHeader>
					<DialogTitle>{capitalize(type)} to...</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 mt-4">
					{/* Breadcrumbs */}
					<div className="flex flex-wrap items-center text-sm text-muted-foreground">
						<div
							role="button"
							data-disabled={!isAdmin}
							onClick={() =>
								isAdmin &&
								onBreadcrumbClick({ type: 'instance' })
							}
							className="cursor-pointer hover:underline data-[disabled=true]:cursor-default data-[disabled=true]:hover:no-underline"
						>
							ASTA
						</div>
						{breadcrumbResources.map(res => (
							<div
								key={res._id}
								role="button"
								data-disabled={!canCreate(res.role) && !isAdmin}
								onClick={() =>
									(canCreate(res.role) || isAdmin) &&
									onBreadcrumbClick(res)
								}
								className="cursor-pointer hover:underline data-[disabled=true]:cursor-default data-[disabled=true]:hover:no-underline"
							>
								<span className="mx-1">{'>'}</span>
								{res.name}
							</div>
						))}
						<span className="mx-1">{'>'}</span>
					</div>

					{/* Resource Selection List */}
					<div className="h-36 space-y-1 overflow-y-auto rounded-md border p-2">
						{selectableResources.filter(r => canCreate(r.role))
							.length > 0 ? (
							selectableResources
								.filter(r => canCreate(r.role))
								.map(resource => (
									<div
										key={resource._id}
										role="button"
										onClick={() =>
											onResourceClick(resource)
										}
										className={cn(
											'cursor-pointer rounded-md p-2 text-sm transition-colors hover:bg-accent',
											resource._id === selectedVar?._id &&
												'bg-accent'
										)}
									>
										{resource.name}
									</div>
								))
						) : (
							<p className="p-2 text-sm italic text-muted-foreground">
								No resources found
							</p>
						)}
					</div>

					{/* Name Input */}
					<div className="grid w-full items-center gap-1.5">
						<Label htmlFor="copy-name">Name</Label>
						<Input
							id="copy-name"
							name="name"
							placeholder="Enter Name"
							value={values.name}
							onBlur={getFieldProps('name').onBlur}
							onChange={e =>
								setFieldValue('name', e.target.value)
							}
						/>
						{touched.name && errors.name && (
							<p className="text-sm text-destructive">
								{errors.name}
							</p>
						)}
					</div>
				</div>

				{/* Action Buttons */}
				<div className="mt-6 flex justify-end gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						disabled={
							(!isAdmin && !selectedWorkspace) || isSubmitting
						}
					>
						{capitalize(type)}
					</Button>
				</div>
			</Form>
		</FormikProvider>
	)
}

export default CopyAndMoveForm
